import {FC} from 'react';

import {marketT} from '@/lib/types/market.types';

import {useNotifications} from '@/providers/components/notificationsProvider.tsx';

import {SearchAndSelectComponent} from '@/components';
import Page from '@/components/Page';

import {useCreateDistributorOrder} from '../providers/createDistributorOrderProvider.tsx';
import OrderDetailsComponent from './orderDetailsComponent.tsx';
import OrderFormRowComponent from './orderFormRowComponent.tsx';

/**
 * Create Order form
 * @returns {JSX.Element}
 */
const CreateDistributorOrderForm: FC = () => {
  const {toast} = useNotifications();
  const {
    setAcknowledging,
    acknowledging,
    market,
    distributor,
    setMarket,
    showMarketSearch,
    setShowMarketSearch,
    setSearchTerm,
    validOrderProducts,
    markets,
    orderItems,
    totals,
  } = useCreateDistributorOrder();

  const handleReviewOrder = () => {
    // make sure there is at least one item and that the item is greater than 0
    const hasValidItems = Object.values(orderItems).some(item => item > 0);
    if (!hasValidItems) {
      toast.error('Please add at least one product with a quantity greater than 0.');
      return;
    }
    setAcknowledging(true);
  };

  if (acknowledging) {
    return <OrderDetailsComponent />;
  }

  return (
    <Page currentPage="Create Order">
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-12">
          {!acknowledging ? (
            <div className="box">
              {/* HEADER */}

              <div className="box-header !block xxl:!flex">
                <div className="h5 mb-0 block items-center sm:flex">
                  <div>
                    <img src={'/images/logo/logo.png'} alt="" className="w-10" />
                  </div>
                  <div className="ms-0 mt-2 sm:ms-2 sm:mt-0">
                    <span className="form-control !rounded-md !p-0">{new Date().toLocaleDateString()}</span>
                  </div>
                  <div className="mx-2">:</div>
                  <div className="sm:mt-0">
                    <span className="form-control !rounded-md !p-0">{new Date().toLocaleTimeString()}</span>
                  </div>
                </div>
                <div className="!mt-2 ms-auto flex justify-start gap-2 xxl:!mt-0">
                  <>
                    {distributor && (
                      <>
                        <div className="flex justify-start">
                          <div className="flex items-center rounded-md p-2">
                            {distributor ? (
                              <span className="mr-2 truncate">{distributor.distributor_name}</span>
                            ) : (
                              <span className="mr-2 truncate text-gray-400">No Distributor selected</span>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                    <div className="flex justify-start">
                      <div className="flex items-center rounded-md border-2 border-gray-300 p-2">
                        {market ? (
                          <span className="mr-2 truncate">{market.market_name}</span>
                        ) : (
                          <span className="mr-2 truncate text-gray-400">No Market selected</span>
                        )}
                        <button
                          aria-label="search"
                          className="ti-btn ti-btn-sm ti-btn-light ml-auto"
                          onClick={() => {
                            // setDistributor(null);
                            setShowMarketSearch(true);
                          }}
                        >
                          <i className="ri-search-line"></i>
                        </button>
                      </div>
                    </div>

                    <SearchAndSelectComponent
                      title="Search and Select Market"
                      isVisible={showMarketSearch}
                      onClose={() => {
                        setShowMarketSearch(false);
                      }}
                      onSelect={(item: marketT) => {
                        setMarket(item);
                        setShowMarketSearch(false);
                      }}
                      onSearch={(idx: string) => {
                        setSearchTerm(idx);
                      }}
                      results={markets}
                      mapStructure={{
                        displayFields: [
                          {
                            title: 'Market Name',
                            field: 'market_name',
                          },
                        ],
                        valueField: 'market_id',
                      }}
                    />
                  </>
                </div>
              </div>
              {/* BODY */}
              <div className="box-body">
                {/* Order TABLE */}
                {!distributor ? (
                  <div className="text-center">
                    <p className="text-gray-500">Please select a market and distributor to get started.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-12 gap-y-6 sm:gap-x-6">
                    <div className="col-span-12 xl:col-span-12">
                      <div className="table-responsive">
                        <table className="mt-3 table w-full whitespace-nowrap border dark:border-defaultborder/10">
                          <thead>
                            <tr>
                              <th scope="row">Product</th>
                              <th scope="row">Brand</th>
                              <th scope="row">Product Quantity</th>
                              <th scope="row" className="min-w-[120px]">
                                Order Quantity
                              </th>
                              {/* <th scope="row">Avg Wholesale Price</th> */}
                              <th scope="row">Avg Retail Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {validOrderProducts &&
                              validOrderProducts.map((item, index) => (
                                <OrderFormRowComponent key={index} item={item} />
                              ))}
                          </tbody>
                          <tfoot>
                            <tr className="font-semibold">
                              <td colSpan={1}>Totals: {totals?.total_count || 0}</td>
                              <td>{totals?.unique_brand_count || 0}</td>
                              <td>{validOrderProducts?.reduce((sum, item) => sum + item.item_count, 0)}</td>
                              <td
                                colSpan={1}
                                style={{
                                  textAlign: 'center',
                                }}
                              >
                                {Object.values(orderItems).reduce((sum, quantity) => sum + quantity, 0)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="box-footer text-end">
                {validOrderProducts && validOrderProducts.length > 0 && (
                  <button type="button" className="ti-btn !mb-0 bg-primary text-white" onClick={handleReviewOrder}>
                    Review Order{validOrderProducts.length > 1 ? 's' : ''}{' '}
                    <i className="ri-send-plane-2-line ms-1 inline-block align-middle"></i>
                  </button>
                )}
              </div>
            </div>
          ) : (
            <OrderDetailsComponent />
          )}
        </div>
      </div>
    </Page>
  );
};

export default CreateDistributorOrderForm;
